import React, { useContext, useState, useEffect } from "react";
import { ArrowsClockwiseIcon, ExportIcon } from "@phosphor-icons/react";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { toastOptions } from "../../assets/constants";
import { AuthContext } from "../../contexts";
import { CustomPageHeader, CustomList, CustomTag } from "../../ui";

import pennylaneLogo from "../../assets/icons/pennylane.svg";

const BillingSubscription = () => {
    const [isExporting, setIsExporting] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [subscriptions, setSubscriptions] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
    const { token } = useContext(AuthContext);

    const fetchSubscriptions = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}pennylane/subscriptions`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            let subscriptionsData = response.data;

            if (Array.isArray(subscriptionsData)) {
                subscriptionsData = subscriptionsData.sort((a, b) => {
                    const idA = parseInt(a.customer.reference, 10) || 0;
                    const idB = parseInt(b.customer.reference, 10) || 0;
                    return idB - idA;
                });
                setSubscriptions(subscriptionsData);
                setFilteredSubscriptions(subscriptionsData);
                setLastUpdatedAt(response.data.length > 0 ? response.data[0].updatedAt : null);
                setIsLoaded(true);
            } else {
                toast.error("Les données des abonnements ne sont pas valides.", toastOptions);
                setIsLoaded(true);
            }
        } catch (error) {
            toast.error("Erreur lors de la récupération des abonnements", toastOptions);
            setIsLoaded(true);
        }
    };

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    useEffect(() => {
        const results = subscriptions.filter((subscription) => subscription.customer.name.toLowerCase().includes(searchTerm.toLowerCase()));
        setFilteredSubscriptions(results);
    }, [searchTerm, subscriptions]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const syncData = async () => {
        setIsSyncing(true);
        try {
            await axios.get(`${process.env.REACT_APP_UPA_API_HOST}pennylane/sync-subscriptions`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Synchronisation réussie", toastOptions);
            await fetchSubscriptions();
        } catch (error) {
            toast.error("Erreur lors de la synchronisation", toastOptions);
        } finally {
            setIsSyncing(false);
        }
    };

    const downloadCSV = async () => {
        setIsExporting(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}pennylane/export-subscriptions`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            const currentDate = new Date();
            const formattedDate = currentDate
                .toLocaleDateString("fr-FR", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                })
                .replace(/\//g, "_");
            link.setAttribute("download", `abonnements_pennylane_${formattedDate}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success("Fichier téléchargé avec succès.", toastOptions);
        } catch (error) {
            toast.error("Erreur lors du téléchargement du fichier", toastOptions);
        } finally {
            setIsExporting(false);
        }
    };

    const getStatusInfo = (status) => {
        // "draft" et "not_started" désignent tous deux un abonnement non commencé :
        // même libellé, même couleur (sinon la liste affiche du gris ET de l'orange).
        const mapping = {
            "finished": { label: "Terminé", className: "deactivated" },
            "not_started": { label: "Non commencé", className: "not-started" },
            "draft": { label: "Non commencé", className: "not-started" },
            "in_progress": { label: "En cours", className: "paid" },
            "stopped": { label: "Inactif", className: "unpaid" },
        };

        return mapping[status] || { label: status || "Inconnu", className: "deactivated" };
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "ID",
                accessor: "customer.reference",
                icon: "id",
                Cell: ({ value }) => <CustomTag value={value || "Non disponible"} />,
            },
            {
                Header: "Nom du client",
                accessor: "customer.name",
                icon: "user",
                Cell: ({ value }) => value || "Non disponible",
            },
            {
                Header: "Date de début",
                accessor: "start",
                icon: "calendar",
                Cell: ({ value }) => <CustomTag value={value ? dayjs(value).format("DD MMMM YYYY") : "Non disponible"} />,
            },
            {
                Header: "Statut",
                accessor: "status",
                icon: "tag-simple",
                Cell: ({ value }) => {
                    const statusInfo = getStatusInfo(value);
                    return <CustomTag value={statusInfo.label} className={statusInfo.className} />;
                },
            },
        ],
        []
    );

    return isLoaded ? (
        <div className="c-app__view">
            <CustomPageHeader
                title="Abonnements"
                titleIcon={{
                    svg: pennylaneLogo,
                    size: 30,
                }}
                lastUpdatedAt={lastUpdatedAt}
                buttons={[
                    {
                        label: "Exporter",
                        onClick: downloadCSV,
                        variant: "secondary",
                        icon: ExportIcon,
                        disabled: isExporting,
                        loading: isExporting,
                    },
                    {
                        label: "Synchroniser",
                        onClick: syncData,
                        variant: "primary",
                        icon: ArrowsClockwiseIcon,
                        disabled: isSyncing,
                        loading: isSyncing,
                    },
                ]}
            />
            <div className="input-container w-20">
                <div className="single-search-input">
                    <input type="text" placeholder="Rechercher un client..." value={searchTerm} onChange={handleSearchChange} className="search-input" />
                </div>
            </div>
            <div className="tabs-container">
                <CustomList entity={filteredSubscriptions} columns={columns} isSelectable={false} isClickable={false} />
            </div>
        </div>
    ) : (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
    );
};

export default BillingSubscription;
